// Domain types for the Prompt Library. Backed by the real `prompt-service` —
// every enum is canonical in `@atlas/types` (backend + frontend share one source of
// truth), re-exported here so feature code imports from one place.
import type {
	DTOOf,
	IPrompt,
	IPromptVariableList,
	IPromptVersion,
	PromptModelConfig,
	PromptRenderResult,
	PromptTestCaseSnapshot,
	PromptVariable,
	PromptVariableListItem,
} from '@atlas/types'
import {
	EvalCaseOutcome,
	EvalRunStatus,
	PromptAssertionType,
	PromptLabel,
	PromptLifecycle,
	PromptOutputMode,
	PromptTestCaseOrigin,
	PromptType,
	PromptVariableType,
} from '@atlas/types'

export type { PromptModelConfig, PromptRenderResult, PromptTestCaseSnapshot, PromptVariable, PromptVariableListItem }
export {
	EvalCaseOutcome,
	EvalRunStatus,
	PromptAssertionType,
	PromptLabel,
	PromptLifecycle,
	PromptOutputMode,
	PromptTestCaseOrigin,
	PromptType,
	PromptVariableType,
}

export type Prompt = DTOOf<IPrompt>
export type PromptListRecord = DTOOf<IPromptVariableList>
export type PromptVersion = DTOOf<IPromptVersion>

export const PROMPT_LIFECYCLE_LABEL: Record<PromptLifecycle, string> = {
	[PromptLifecycle.DRAFT]: 'Draft',
	[PromptLifecycle.PUBLISHED]: 'Published',
	[PromptLifecycle.ARCHIVED]: 'Archived',
}

/** Border + text colour per lifecycle, matching the Rules explorer's convention. */
export const PROMPT_LIFECYCLE_COLOR: Record<PromptLifecycle, string> = {
	[PromptLifecycle.DRAFT]: 'border-ds-border text-ds-text-muted',
	[PromptLifecycle.PUBLISHED]: 'border-ds-success text-ds-success',
	[PromptLifecycle.ARCHIVED]: 'border-ds-warning text-ds-warning',
}

export const PROMPT_TYPE_LABEL: Record<PromptType, string> = {
	[PromptType.USER_USE_CASE]: 'User use case',
	[PromptType.SYSTEM_USE_CASE]: 'System use case',
	[PromptType.VALIDATION]: 'Validation',
}

/** What each type means, for the picker — the audience is not obvious from the name. */
export const PROMPT_TYPE_HINT: Record<PromptType, string> = {
	[PromptType.USER_USE_CASE]: 'A person picks it and fills its variables',
	[PromptType.SYSTEM_USE_CASE]: 'The router injects it; code supplies its variables',
	[PromptType.VALIDATION]: 'A governance check — runs at temperature 0',
}

/** Explorer facet ordering — the types authors reach for most, first. */
export const PROMPT_TYPE_ORDER: readonly PromptType[] = [
	PromptType.USER_USE_CASE,
	PromptType.SYSTEM_USE_CASE,
	PromptType.VALIDATION,
] as const

export const PROMPT_LIFECYCLE_ORDER: readonly PromptLifecycle[] = [
	PromptLifecycle.DRAFT,
	PromptLifecycle.PUBLISHED,
	PromptLifecycle.ARCHIVED,
] as const

export const PROMPT_VARIABLE_TYPE_LABEL: Record<PromptVariableType, string> = {
	[PromptVariableType.STRING]: 'Short text',
	[PromptVariableType.TEXT]: 'Long text',
	[PromptVariableType.NUMBER]: 'Number',
	[PromptVariableType.BOOLEAN]: 'Yes / no',
	[PromptVariableType.DATE]: 'Date',
	[PromptVariableType.ENUM]: 'Fixed options',
	[PromptVariableType.LIST]: 'Shared list',
	[PromptVariableType.EACH]: 'Repeated items',
	[PromptVariableType.JOIN]: 'Joined fields',
}

export const ASSERTION_TYPE_LABEL: Record<PromptAssertionType, string> = {
	[PromptAssertionType.CONTAINS]: 'Must contain',
	[PromptAssertionType.NOT_CONTAINS]: 'Must not contain',
	[PromptAssertionType.MATCHES_OUTPUT_SCHEMA]: 'Matches output schema',
	[PromptAssertionType.MAX_LENGTH]: 'Max length',
	[PromptAssertionType.LLM_RUBRIC]: 'Judged criterion',
}

/** A run is settled once it can no longer change — what the UI stops polling on. */
export const SETTLED_RUN_STATUSES: readonly EvalRunStatus[] = [
	EvalRunStatus.COMPLETED,
	EvalRunStatus.FAILED,
	EvalRunStatus.CANCELLED,
] as const

export const isRunSettled = (status: EvalRunStatus): boolean => SETTLED_RUN_STATUSES.includes(status)

/**
 * Whether a variable takes a value from whoever fills the prompt.
 *
 * A `join` does not: it is COMPUTED from the variables it names, so offering a field
 * for it asks for something that would be overwritten, and every caller that built a
 * form had to remember to exclude it. Deriving this from the type rather than from a
 * per-variable flag means an author cannot forget to set it, and a new computed type
 * is handled by editing this one predicate.
 *
 * An `each` DOES take a value — an array supplied by code — so it stays fillable.
 */
export const isFillable = (variable: PromptVariable): boolean => variable.type !== PromptVariableType.JOIN

/** The variables a form should offer, in the order the prompt declares them. */
export const fillableVariables = (variables: PromptVariable[]): PromptVariable[] => variables.filter(isFillable)

/**
 * Whether a variable's control needs a row of its own.
 *
 * Derived from the TYPE rather than from an authored layout flag, so every prompt
 * benefits — including ones written before this existed — and an author never has to
 * think about presentation. A `divider` variable type would have put layout into the
 * prompt's data model and left existing prompts ragged until someone edited them.
 *
 * A multi-line control in a shared row forces the whole row to its height and leaves
 * the short fields beside it floating against the top, which is what made the
 * generation form look unfinished.
 */
export const isWideControl = (variable: PromptVariable): boolean => variable.type === PromptVariableType.TEXT

/**
 * Variables a RUNTIME caller supplies rather than a person.
 *
 * An `each` is an array of objects assembled by code — governance rules, retrieved
 * chunks. A form names these so a reader knows the prompt expects them, but offers no
 * control: the previous JSON textarea asked a person to hand-write an array of
 * objects, which is not a workflow anyone has (ADR-0034).
 */
export const injectedVariables = (variables: PromptVariable[]): PromptVariable[] =>
	variables.filter((v) => v.type === PromptVariableType.EACH)

/**
 * Fillable variables split into compact fields and full-width ones, each keeping the
 * prompt's declared order.
 *
 * Compact first regardless of declaration order: a form reads better when the
 * one-line choices are grouped and the prose sits below them, and the prompt's own
 * order is preserved WITHIN each group — which is the part that matters, since that
 * order is also the render order.
 */
export const groupedFields = (variables: PromptVariable[]): { compact: PromptVariable[]; wide: PromptVariable[] } => {
	const fields = fillableVariables(variables)

	// `each` is excluded outright: it is named elsewhere, not offered as a field.
	const offered = fields.filter((v) => v.type !== PromptVariableType.EACH)

	return {
		compact: offered.filter((v) => !isWideControl(v)),
		wide: offered.filter(isWideControl),
	}
}

/**
 * The variable types an author may declare on a prompt of this type.
 *
 * `each` is a JSON array of OBJECTS supplied by code — governance rules, retrieved
 * chunks, prior turns. Offering it on a prompt a person fills asks them to hand-write
 * that array, which is not a workflow anyone has; the work-order context form did
 * exactly that and rendered a JSON textarea nobody could sensibly complete (ADR-0034).
 */
export const variableTypesFor = (promptType: PromptType): PromptVariableType[] =>
	promptType === PromptType.SYSTEM_USE_CASE
		? Object.values(PromptVariableType)
		: Object.values(PromptVariableType).filter((t) => t !== PromptVariableType.EACH)

export interface PromptAssertion {
	type: PromptAssertionType
	value?: string
	max?: number
}

export interface PromptTestCase {
	id: string
	promptId: string
	name: string
	values: Record<string, unknown>
	assertions: PromptAssertion[]
	origin: PromptTestCaseOrigin
	isActive: boolean
	createdAt: string
	updatedAt: string
}

export interface EvalCaseResult {
	testCaseId: string
	name: string
	outcome: EvalCaseOutcome
	detail?: unknown
}

export interface EvalRun {
	id: string
	promptId: string
	promptVersionId: string | null
	status: EvalRunStatus
	totalCases: number
	completedCases: number
	passedCases: number
	failedCases: number
	erroredCases: number
	results: EvalCaseResult[]
	failureReason: string | null
	triggeredBy: string | null
	createdAt: string
	settledAt: string | null
}

export interface PromptLabelPointer {
	id: string
	promptId: string
	label: PromptLabel
	promptVersionId: string
	pinned: boolean
	updatedBy: string | null
	updatedAt: string
}

/**
 * Body for creating a prompt. Mirrors the backend's Zod-inferred
 * `PromptCreateInput` field-for-field — the frontend cannot import that type (it is
 * inferred from a backend-only schema), so keep this in sync by hand when the
 * backend schema changes.
 */
export interface CreatePromptInput {
	code: string
	name: string
	description?: string
	type: PromptType
	body: string
	variables?: PromptVariable[]
	outputSchema?: Record<string, unknown>
}

export type UpdatePromptInput = Partial<Omit<CreatePromptInput, 'code' | 'outputSchema'>> & {
	lifecycle?: PromptLifecycle
	/** Per-prompt overrides; an omitted field inherits the tenant default. */
	modelConfig?: Partial<PromptModelConfig>
	/** `null` clears it, which is how a prompt returns to plain-text output. */
	outputSchema?: Record<string, unknown> | null
}

/**
 * Whether a variable value counts as "not filled in".
 *
 * ONE definition, because the two sides of a form have to agree. The required-field
 * check tested only `undefined` while the payload builder also stripped `''`, so
 * clearing a Combobox — which sets `''`, not `undefined` — read as filled, kept Save
 * enabled, and then sent a payload the server refused with
 * `Missing required variable`.
 *
 * An empty array is empty too: a multi-select cleared to `[]` has nothing chosen.
 */
export const isBlankVariableValue = (value: unknown): boolean =>
	value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)

/** The values worth sending — blanks are omitted so the render engine applies defaults. */
export const withoutBlankValues = (values: Record<string, unknown>): Record<string, unknown> =>
	Object.fromEntries(Object.entries(values).filter(([, v]) => !isBlankVariableValue(v)))

/**
 * What a placeholder IS, for every frontend site that has to decide.
 *
 * ONE definition, exported, because three copies is what let them drift. It mirrors
 * `PLACEHOLDER_PATTERN` in the render service — which is the authority, since it is
 * the regex that actually substitutes — and cannot import it: that module is a Lambda
 * service, and pulling it into the browser bundle would drag the backend with it.
 *
 * Case-SENSITIVE, and deliberately NO dot. A dot is only meaningful inside an `each`
 * item template, where the item exposes fields; in a body it is not a placeholder at
 * all. The highlighter allowed one and the other two did not, so `{{subject.name}}`
 * rendered as a valid-looking token, passed the lint, saved, and then reached the
 * model as literal braces.
 */
const PLACEHOLDER_KEY = String.raw`[A-Za-z_][A-Za-z0-9_]*`

/** `{{key}}`, whitespace tolerated — a fresh instance per call, never a shared global. */
export const placeholderRegex = (): RegExp => new RegExp(String.raw`\{\{\s*(${PLACEHOLDER_KEY})\s*\}\}`, 'g')

/** `{{key}}` as a SPLIT pattern: the capturing group is what makes `split` keep them. */
export const placeholderSplitRegex = (): RegExp => new RegExp(String.raw`(\{\{\s*${PLACEHOLDER_KEY}\s*\}\})`, 'g')

/** One part, anchored — for testing a single token rather than scanning a body. */
export const isPlaceholderToken = (part: string): boolean =>
	new RegExp(String.raw`^\{\{\s*${PLACEHOLDER_KEY}\s*\}\}$`).test(part)

/**
 * Every `{{placeholder}}` a body references.
 *
 * Shared by the editor's save-time lint, the preview form and the highlighter, so the
 * three can never disagree about what the body actually asks for.
 */
export function placeholdersIn(body: string): string[] {
	return [...new Set([...body.matchAll(placeholderRegex())].map((m) => m[1] as string))]
}

export interface PromptLintResult {
	/** Referenced by the body but not defined — a render would fail. */
	undeclared: string[]
	/** Defined but never referenced — collected from callers and then ignored. */
	unused: string[]
}

/**
 * Lint a body against its variable schema.
 *
 * Undeclared placeholders are an error (the backend refuses the save); unused
 * variables are a warning, since they are collected from callers and then silently
 * dropped, which is confusing rather than broken.
 *
 * A variable is USED when the body names it OR a `join` composes it. Checking the
 * body alone reported every part of a composed prompt as ignored: a body of
 * `{{direction}}` whose join pulls in a subject, a brief and four qualifiers named
 * all seven as "collected from callers and then ignored" — the exact opposite of
 * what the render engine does with them.
 */
export function lintPrompt(body: string, variables: PromptVariable[]): PromptLintResult {
	const inBody = placeholdersIn(body)
	// Transitively: a join naming another join means the inner one's parts are used
	// too, and only the outermost appears in the body.
	const joined = new Set(variables.flatMap((v) => v.joins ?? []))
	const used = new Set([...inBody, ...joined])

	const declared = variables.map((v) => v.key)
	return {
		undeclared: inBody.filter((k) => !declared.includes(k)).sort(),
		unused: declared.filter((k) => !used.has(k)).sort(),
	}
}

/**
 * The eval state a publisher sees before publishing.
 *
 * `blocking` is the tenant setting; `wouldBlock` is that setting applied to THIS
 * prompt's last result. The dialog needs both: one explains why a publish is
 * refused, the other whether it will be.
 */
/** The last settled eval result for a version, as the API serialises it. */
export interface PromptEvalSummary {
	passed: number
	failed: number
	errored: number
	ranAt: string
	ranBy?: string
}

export interface PromptEvalGate {
	summary: PromptEvalSummary | null
	blocking: boolean
	wouldBlock: boolean
	warning: string | null
}
